import { principalKeyOf, useSessionStore } from "@auth/sessionStore";
import {
  fetchPhoneCall,
  fetchPhoneCalls,
  isFresherPhoneCall,
  type PhoneCall,
  type PhoneCallPage,
} from "@phone/phoneCallsClient";
import {
  type InfiniteData,
  type QueryClient,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query";

export const phoneCallsKeys = {
  /** Invalidation only; a row write goes to one session's `list`. */
  all: ["phone-calls"] as const,
  list: (sessionKey: string) => ["phone-calls", sessionKey, "list"] as const,
  /** One call outside the loaded log pages; refreshed by its own `phone_call` frames. */
  call: (sessionKey: string, callId: string) =>
    ["phone-calls", sessionKey, "call", callId] as const,
};

type PhoneCallsData = InfiniteData<PhoneCallPage, string | undefined>;

/** No polling: `phone_call` frames patch rows in place, and a socket reconnect invalidates. */
export const usePhoneCalls = (sessionKey: string) =>
  useSuspenseInfiniteQuery({
    queryKey: phoneCallsKeys.list(sessionKey),
    queryFn: ({ pageParam }) => fetchPhoneCalls(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

/**
 * A read outlives logout and `SessionCacheReset`, so its row lands only while
 * its principal is still signed in; otherwise a late row would leak the previous
 * clinic's caller identity, summary or message into the next principal's cache.
 */
export interface PhoneCallsSession {
  readonly principal: string;
  readonly sessionKey: string;
}

export const currentPhoneCallsSession = (): PhoneCallsSession => {
  const { user, clinic } = useSessionStore.getState();
  return {
    principal: principalKeyOf(user ?? clinic),
    sessionKey: user?.userUuid ?? "",
  };
};

const isCurrentSession = (session: PhoneCallsSession): boolean =>
  currentPhoneCallsSession().principal === session.principal;

const createdMs = (call: PhoneCall): number =>
  new Date(call.createdAt).getTime();

/**
 * An equal revision still replaces: brian writes the summary without bumping it.
 * An unknown call goes in at its newest-first position, or nowhere if its page is not loaded.
 * A row whose session is no longer signed in is dropped.
 */
export const patchPhoneCall = (
  queryClient: QueryClient,
  call: PhoneCall,
  session: PhoneCallsSession,
): void => {
  if (!isCurrentSession(session)) return;
  queryClient.setQueryData<PhoneCallsData>(
    phoneCallsKeys.list(session.sessionKey),
    (data) => {
      if (!data) return undefined;
      const { pages } = data;
      const withPage = (index: number, calls: PhoneCall[]): PhoneCallsData => ({
        ...data,
        pages: pages.map((page, p) =>
          p === index ? { ...page, calls } : page,
        ),
      });
      for (let p = 0; p < pages.length; p += 1) {
        const calls = pages[p].calls;
        const i = calls.findIndex((c) => c.id === call.id);
        if (i === -1) continue;
        if (!isFresherPhoneCall(calls[i], call, { allowEqual: true })) {
          return undefined;
        }
        return withPage(p, [...calls.slice(0, i), call, ...calls.slice(i + 1)]);
      }
      const when = createdMs(call);
      for (let p = 0; p < pages.length; p += 1) {
        const calls = pages[p].calls;
        const i = calls.findIndex((c) => createdMs(c) < when);
        if (i === -1) continue;
        return withPage(p, [...calls.slice(0, i), call, ...calls.slice(i)]);
      }
      const last = pages.length - 1;
      if (last < 0 || pages[last].nextCursor !== null) return undefined;
      return withPage(last, [...pages[last].calls, call]);
    },
  );
};

const RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

/** One session's row reads: dropped whole — aborted, timers cleared — when it signs out. */
interface SessionRefreshes {
  readonly session: PhoneCallsSession;
  readonly controller: AbortController;
  readonly inflight: Set<string>;
  readonly queued: Set<string>;
  readonly retryTimers: Set<ReturnType<typeof setTimeout>>;
}

let refreshes: SessionRefreshes | null = null;

const dropRefreshes = (): void => {
  if (!refreshes) return;
  refreshes.controller.abort();
  for (const timer of refreshes.retryTimers) clearTimeout(timer);
  refreshes = null;
};

useSessionStore.subscribe(() => {
  if (refreshes && !isCurrentSession(refreshes.session)) dropRefreshes();
});

const sessionRefreshes = (): SessionRefreshes => {
  if (refreshes && isCurrentSession(refreshes.session)) return refreshes;
  dropRefreshes();
  refreshes = {
    session: currentPhoneCallsSession(),
    controller: new AbortController(),
    inflight: new Set(),
    queued: new Set(),
    retryTimers: new Set(),
  };
  return refreshes;
};

/**
 * Frames arriving during an in-flight read coalesce into one follow-up read.
 * An inactive log is only marked stale, not read.
 */
export const refreshPhoneCall = (
  queryClient: QueryClient,
  callUuid: string,
): void => {
  const work = sessionRefreshes();
  const listKey = phoneCallsKeys.list(work.session.sessionKey);
  const active = queryClient
    .getQueryCache()
    .findAll({ queryKey: listKey, type: "active" });
  if (active.length === 0) {
    void queryClient.invalidateQueries({
      queryKey: listKey,
      refetchType: "none",
    });
    return;
  }
  if (work.inflight.has(callUuid)) {
    work.queued.add(callUuid);
    return;
  }
  work.inflight.add(callUuid);
  void readPhoneCallRow(queryClient, work, callUuid, 0);
};

/** Out of retries, the whole log is re-read: the final summary/message write has no next frame. */
const readPhoneCallRow = async (
  queryClient: QueryClient,
  work: SessionRefreshes,
  callUuid: string,
  attempt: number,
): Promise<void> => {
  let call: PhoneCall;
  try {
    call = await fetchPhoneCall(callUuid, work.controller.signal);
  } catch {
    if (work !== refreshes) return;
    if (attempt < RETRY_DELAYS_MS.length) {
      const timer = setTimeout(() => {
        work.retryTimers.delete(timer);
        void readPhoneCallRow(queryClient, work, callUuid, attempt + 1);
      }, RETRY_DELAYS_MS[attempt]);
      work.retryTimers.add(timer);
      return;
    }
    work.inflight.delete(callUuid);
    work.queued.delete(callUuid);
    void queryClient.invalidateQueries({
      queryKey: phoneCallsKeys.list(work.session.sessionKey),
    });
    return;
  }
  if (work !== refreshes) return;
  patchPhoneCall(queryClient, call, work.session);
  work.inflight.delete(callUuid);
  if (work.queued.delete(callUuid)) refreshPhoneCall(queryClient, callUuid);
};
